"use client";

import { isIconName } from "@/lib/icons/Icon";
import type { UserAccountExtension, UserAccountAction } from "@/lib/api/user-account-extensions";
import { TabHeader } from "./TabHeader";
import {
  CPrivateMessagesView,
  cPrivateMessagesHandlesAction,
} from "./plugins/CPrivateMessages/CPrivateMessagesView";
import {
  CPhotoGalleryView,
  cPhotoGalleryHandlesAction,
} from "./plugins/CPhotoGallery/CPhotoGalleryView";
import {
  CFactureView,
  cFactureHandlesAction,
} from "./plugins/CFacture/CFactureView";
import {
  CElectronicSignsView,
  cElectronicSignsHandlesAction,
} from "./plugins/CElectronicSigns/CElectronicSignsView";
import {
  CProceduresView,
  cProceduresHandlesAction,
} from "./plugins/CProcedures/CProceduresView";
import {
  CResourceReservationView,
  cResourceReservationHandlesAction,
} from "./plugins/CResourceReservation/CResourceReservationView";
import {
  CAdvertisementsView,
  cAdvertisementsHandlesAction,
} from "./plugins/CAdvertisements/CAdvertisementsView";
import {
  CDedicatedApplicationFormsView,
  cDedicatedApplicationFormsHandlesAction,
} from "./plugins/CDedicatedApplicationForms/CDedicatedApplicationFormsView";
import styles from "./ExtensionActionView.module.css";

/**
 * Main area view dla aktywnej akcji rozszerzenia.
 *
 * Renderowany w `ProfileTabs` gdy URL zawiera `#extension-{action_id}` w hash.
 * Dispatch per plugin:
 *   - `c_private_messages` -> `<CPrivateMessagesView actionId>` (inbox/sent listy)
 *   - inne pluginy / nieobsługiwane akcje -> placeholder z dev info
 *
 * **Przyszłe iteracje:** kolejne pluginy dodają własne dispatchery (analogicznie
 * do `cPrivateMessagesHandlesAction` + `<CPrivateMessagesView>`).
 */
export function ExtensionActionView({
  extension,
  actionId,
  matchedAction,
}: {
  extension: UserAccountExtension;
  actionId: string;
  matchedAction: { key: string; action: UserAccountAction } | null;
}) {
  const isDev = process.env.NODE_ENV === "development";

  const headerIcon = matchedAction && isIconName(matchedAction.action.icon)
    ? matchedAction.action.icon
    : "puzzle-piece";

  /* Plugin dispatch — registry per symbol. Każdy plugin może obsłużyć podzbiór
     swoich actions; reszta spada do fallback placeholder. */
  const hasRenderer =
    (extension.symbol === "c_private_messages" && cPrivateMessagesHandlesAction(actionId))
    || (extension.symbol === "c_photo_gallery" && cPhotoGalleryHandlesAction(actionId))
    || (extension.symbol === "c_facture" && cFactureHandlesAction(actionId))
    || (extension.symbol === "c_electronic_signs" && cElectronicSignsHandlesAction(actionId))
    || (extension.symbol === "c_procedures" && cProceduresHandlesAction(actionId))
    || (extension.symbol === "c_resource_reservation" && cResourceReservationHandlesAction(actionId))
    || (extension.symbol === "c_advertisements" && cAdvertisementsHandlesAction(actionId))
    || (extension.symbol === "c_dedicated_application_forms" && cDedicatedApplicationFormsHandlesAction(actionId));

  return (
    <section className={styles.extView}>
      <TabHeader
        icon={headerIcon}
        title={matchedAction?.action.label ?? actionId}
        parent={extension.name}
      />

      <div className={hasRenderer ? styles.extViewContent : styles.extViewBody}>
        {extension.symbol === "c_private_messages" && cPrivateMessagesHandlesAction(actionId) ? (
          <CPrivateMessagesView actionId={actionId} />
        ) : extension.symbol === "c_photo_gallery" && cPhotoGalleryHandlesAction(actionId) ? (
          /* key={actionId} - remount przy zmianie akcji (galleries <-> new-gallery),
             zeby wewnetrzny `mode` startowal od nowa wg aktualnej akcji. */
          <CPhotoGalleryView key={actionId} actionId={actionId} />
        ) : extension.symbol === "c_facture" && cFactureHandlesAction(actionId) ? (
          <CFactureView />
        ) : extension.symbol === "c_electronic_signs" && cElectronicSignsHandlesAction(actionId) ? (
          <CElectronicSignsView />
        ) : extension.symbol === "c_procedures" && cProceduresHandlesAction(actionId) ? (
          <CProceduresView />
        ) : extension.symbol === "c_resource_reservation" && cResourceReservationHandlesAction(actionId) ? (
          <CResourceReservationView />
        ) : extension.symbol === "c_advertisements" && cAdvertisementsHandlesAction(actionId) ? (
          /* key={actionId} - remount przy zmianie akcji (adverts <-> new-advert),
             zeby wewnetrzny `mode` startowal od nowa wg aktualnej akcji. */
          <CAdvertisementsView key={actionId} actionId={actionId} />
        ) : extension.symbol === "c_dedicated_application_forms" && cDedicatedApplicationFormsHandlesAction(actionId) ? (
          /* key={actionId} - remount przy przejsciu my-applications <-> new-application. */
          <CDedicatedApplicationFormsView key={actionId} actionId={actionId} />
        ) : (
          <>
            <p className={styles.extViewPlaceholder}>
              Widok dla tej akcji nie jest jeszcze zaimplementowany w Imperial CMS.
            </p>
            {isDev && (
              <pre className={styles.extViewDevInfo}>
                <strong>Dev info:</strong>{"\n"}
                plugin symbol: {extension.symbol}{"\n"}
                action_id: {actionId}{"\n"}
                URL action key: extension-{actionId}{"\n"}
                {matchedAction
                  ? `matched action: ${matchedAction.key} (label: "${matchedAction.action.label}")`
                  : `(brak action o id "${actionId}" w extension.actions)`}{"\n\n"}
                <strong>Co dodać:</strong>{"\n"}
                • React component renderujący tę akcję{"\n"}
                • Dispatch case w `ExtensionActionView` (registry pattern)
              </pre>
            )}
          </>
        )}
      </div>
    </section>
  );
}
